import { Component, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { type IConfig } from '@onlyoffice/document-editor-angular';
import { NgxSpinnerService } from 'ngx-spinner';
import { BehaviorSubject } from 'rxjs';
import { AppService } from '@app/app.service';
import { MoaService } from '../services/moa.service';
import { MoaRequestsService } from '../services/moa-requests.service';
import { AppBreadcrumbsService } from '@app/layout/breadcrumbs/services/app-breadcrumbs.service';

declare const DocsAPI: any;

@Component({
    selector: 'app-office-editor',
    templateUrl: 'moa.component.html',
    styleUrls: ['moa.component.scss'],
    standalone: true,
})
export class MoaComponent {
    private serviceBreadcrumbs = inject(AppBreadcrumbsService);
    private serviceMOA = inject(MoaService);
    private module: string;
    private factor_id: number;
    private factor_name: string;
    private factor_data_id: string;
    private factor_data_name: string;
    private report_reglam_id: number;
    private report_reglam_name: string;

    /**
     * Ответ от Rest Api
     */
    public rawResponse: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    /**
     * Конфигурация для редактора
     * Значения берет из Rest Api
     */
    public config: IConfig;

    /**
     * Ресурсы из MOA
     */
    public docEditor: any;

    /**
     * Проверка, что компонент DocsAPI загружен
     * Значение по умолчанию true
     * @private
     */
    private hasDocsApi: boolean = true;

    constructor(
        private serviceApp: AppService,
        private route: ActivatedRoute,
        private serviceMOARequest: MoaRequestsService,
        public spinner: NgxSpinnerService,
    ) {
        this.module = this.route.snapshot.queryParams['module'];
        this.factor_id = this.route.snapshot.queryParams['factor_id'];
        this.factor_name = this.route.snapshot.queryParams['factor_name'];
        this.factor_data_id = this.route.snapshot.queryParams['factor_data_id'];
        this.factor_data_name = this.route.snapshot.queryParams['factor_data_name'];
        this.report_reglam_id = this.route.snapshot.queryParams['report_reglam_id'];
        this.report_reglam_name = this.route.snapshot.queryParams['report_reglam_name'];
    }

    async ngAfterViewInit(): Promise<void> {
        await this.serviceMOA.loadScript().then(() => {});
        this.setHasDocsApi();
        if (this.hasDocsApi) {
            switch (this.module) {
                case 'exportReportReglam':
                    await this.runEditorForReportReglam();
                    break;
                default:
                    await this.runDefaultEditor();
            }
        } else {
            this.serviceMOA.notify('error', this.serviceApp.language_dictionary?.notLoadEditorMOA);
        }
    }

    ngOnDestroy(): void {
        if (this.docEditor) {
            this.docEditor.destroyEditor();
        }
    }

    private setHasDocsApi(): void {
        this.hasDocsApi = typeof DocsAPI !== 'undefined';
    }

    private async runDefaultEditor(): Promise<void> {
        const { id, ext } = this.route.snapshot.params;

        await this.spinner.show();

        try {
            const [pageInfo, settings] = await Promise.all([
                this.serviceMOARequest.getPageInfo(id).toPromise(),
                this.serviceMOARequest.getSettings(id, ext).toPromise(),
            ]);

            // Строим хлебные крошки с учетом контекста
            if (pageInfo?.data?.name) {
                this.setBreadcrumbs(pageInfo.data.name);
            }

            if (settings?.data) {
                this.config = settings.data;
                this.docEditor = new DocsAPI.DocEditor('editor', this.config);
            }
        } catch (error) {
            this.serviceMOA.notify('error', this.serviceApp.language_dictionary?.errorConfigEditorMOA);
        } finally {
            await this.spinner.hide();
        }
    }

    private async runEditorForReportReglam(): Promise<void> {
        const { factor_id, report_reglam_id } = this.route.snapshot.params;
        const { factor_name, id_props_type, id_props_type_rubricator, is_link_prop, is_map, name } =
            this.route.snapshot.queryParams;

        this.factor_id = factor_id;
        this.report_reglam_id = report_reglam_id;

        const payload = {
            id: report_reglam_id,
            name: name,
            factor_id: factor_id,
            factor_name: factor_name,
            id_props_type: id_props_type,
            is_link_prop: is_link_prop,
            id_props_type_rubricator: id_props_type_rubricator,
            is_map: is_map,
        };

        await this.spinner.show();

        try {
            const settings = await this.serviceMOARequest.getConfigFromReportReglam(payload).toPromise();

            this.setBreadcrumbs(name);

            if (settings?.data) {
                this.config = settings.data;
                this.docEditor = new DocsAPI.DocEditor('editor', this.config);
            }
        } catch (error) {
            this.serviceMOA.notify('error', this.serviceApp.language_dictionary?.errorConfigEditorMOA);
        } finally {
            await this.spinner.hide();
        }
    }

    private setBreadcrumbs(setName: string): void {
        const breadcrumbs = this.serviceBreadcrumbs.getBreadcrumbs().slice(0, 1);

        if (this.module === 'factor') {
            breadcrumbs.push({
                name: 'Реестры',
                link: '/factor/input',
            });
            breadcrumbs.push({
                name: this.factor_name,
                link: '/factor/input/' + this.factor_id,
            });
            breadcrumbs.push({
                name: this.factor_data_name,
                link: '/factor/input/row/' + this.factor_data_id,
            });
        } else if (this.module === 'report_reglam') {
            breadcrumbs.push({
                name: 'Шаблон документов',
                link: '/reports/documents',
            });
        } else if (this.module === 'exportReportReglam') {
            breadcrumbs.push({
                name: 'Реестры',
                link: '/factor/input',
            });
            breadcrumbs.push({
                name: this.factor_name,
                link: '/factor/input/' + this.factor_id,
            });
        } else {
            breadcrumbs.push({
                name: 'Модуль Офисных Приложении',
                link: '/reports/documents',
            });
        }

        breadcrumbs.push({
            name: setName,
            link: null,
        });

        this.serviceBreadcrumbs.setBreadcrumbs(breadcrumbs);
    }
}
